import { BlobServiceClient } from "@azure/storage-blob";
import config from "./config";
import appUrls from "./appUrls";

import { store } from "./redux_store";
import { testConstants } from "./constants";
import APIHandler from "../handlers/APIHandler";
import DataHandler from "../handlers/DataHandler";

let SAS_TOKEN_MANAGE = "";
let SAS_URL = "";

class AzureStorageManage {
  constructor() {
    // Upload tracking properties
    this.uploadControllers = new Map(); // Track multiple uploads
    this.uploadProgressCallbacks = new Map();
    this.chunkSize = 4 * 1024 * 1024; // 4MB chunks for optimal performance
    this.maxConcurrentChunks = 3; // Limit concurrent uploads
  }
  async getBlobServiceClient() {
    let blobServiceClient;
    SAS_TOKEN_MANAGE = DataHandler.getFromSession("SAS_TOKEN_MANAGE");
    SAS_URL = appUrls.MANAGE_STORAGE_ACCOUNT_URI + "?" + SAS_TOKEN_MANAGE;
    if (SAS_TOKEN_MANAGE) {
      try {
        blobServiceClient = await new BlobServiceClient(SAS_URL);
      } catch (e) {
        SAS_TOKEN_MANAGE = await this.getSASToken();
        blobServiceClient = await new BlobServiceClient(SAS_URL);
      }
    } else {
      SAS_TOKEN_MANAGE = await this.getSASToken();

      SAS_URL = appUrls.MANAGE_STORAGE_ACCOUNT_URI + "?" + SAS_TOKEN_MANAGE.data;
      blobServiceClient = await new BlobServiceClient(SAS_URL);
    }

    return blobServiceClient;
  }

  async getSASToken() {
    await store.dispatch({ type: testConstants.GET_SAS_TOKEN_REQUEST_MANAGE });
    let requestOptions = {
      method: "GET",
      headers: APIHandler.getHeader(config.azure_ad_config.apis.OAT.name),
    };
    SAS_TOKEN_MANAGE = await fetch(
      `${config.oat_api_url}/manage/azure-tokens/get-manage-sas-token`,
      requestOptions
    ).then(APIHandler.handleResponse);
    DataHandler.setToSession("SAS_TOKEN_MANAGE", SAS_TOKEN_MANAGE.data);
    await store.dispatch({
      type: testConstants.GET_SAS_TOKEN_SUCCESS_MANAGE,
      token: SAS_TOKEN_MANAGE.data,
    });
    return SAS_TOKEN_MANAGE;
  }

  async getContainer(containerName) {
    let blobServiceClient = await this.getBlobServiceClient();
    try {
      let containerClient = await blobServiceClient.getContainerClient(
        containerName
      );
      let isExists = await containerClient.exists();
      if (isExists) {
        return containerClient;
      } else {
        await this.createContainer(containerName);
        return await blobServiceClient.getContainerClient(containerName);
      }
    } catch (e) {
      await this.createContainer(containerName);
      return blobServiceClient.getContainerClient(containerName);
    }
  }

  async createContainer(containerName) {
    try {
      let blobServiceClient = await this.getBlobServiceClient();
      let containerClient = await blobServiceClient.getContainerClient(
        containerName
      );
      let containerCreateResponse = await containerClient.createIfNotExists();
    } catch (e) {
      throw "Azure Blob Container Create Failed";
    }
  }

  // delete container immediately on blobServiceClient
  async deleteContainerImmediately(containerName) {
    let blobServiceClient = await this.getBlobServiceClient();
    const response = await blobServiceClient.deleteContainer(containerName);
    if (!response.errorCode) {
    }
  }

  async uploadBlob(containerName, blobName, blobData) {
    const maxRetries = 3;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        let containerClient = await this.getContainer(containerName);
        let blockBlobClient = await containerClient.getBlockBlobClient(blobName);
        let uploadBlobResponse = await blockBlobClient.uploadData(blobData);
        return uploadBlobResponse.requestId;
      } catch (e) {
        if (attempt < maxRetries) {
          console.warn(`Upload attempt ${attempt} failed. Retrying...`);
          await this.delay(1000); // Wait for 1 second before retrying
        } else {
          throw new Error("Azure Blob Upload Failed after multiple attempts");
        }
      }
    }
  }

   
  async multiUploadBlob(containerName, blobDetail=[]) {
    const maxRetries = 3;
    let containerClient
    const promiseArray = []
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        containerClient = await this.getContainer(containerName);
        promiseArray.push(containerClient)
        break
      } catch (e) {
        if (attempt < maxRetries) {
          await this.delay(1000); // Wait for 1 second before retrying
        } else {
          throw new Error("Azure Blob Upload Failed after multiple attempts");
        }
      }
    }
    if(!!containerClient){
      blobDetail.forEach(async(detail,index) => {
        let blockBlobClient = await containerClient.getBlockBlobClient(detail.blobName);
        let uploadBlobResponse = await blockBlobClient.uploadData(detail.blobData, {
          blobHTTPHeaders: {
            blobContentDisposition: "inline"
          }
        });
        promiseArray.push(blockBlobClient)
        promiseArray.push(uploadBlobResponse)
      })
    }
    return await Promise.all(promiseArray)

  }
  
  // Utility function to introduce a delay
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
/**
   * Generate a streaming-optimized URL for video playback
   * This creates a direct blob URL with SAS token for efficient streaming
   */
  async generateVideoStreamUrl(containerName, blobName) {
    try {
      const encodedBlobName = encodeURIComponent(blobName);
      SAS_TOKEN_MANAGE = DataHandler.getFromSession("SAS_TOKEN_MANAGE");
      
      if (!SAS_TOKEN_MANAGE) {
        const sasToken = await this.getSASToken();
        SAS_TOKEN_MANAGE = sasToken.data;
      }
      
      // Set blob headers for inline display to enable streaming
      await this.setBlobForStreaming(containerName, blobName);
      
      const streamUrl = `${appUrls.MANAGE_STORAGE_ACCOUNT_URI}${containerName}/${encodedBlobName}?${SAS_TOKEN_MANAGE}`;
      return streamUrl;
    } catch (error) {
      console.error('Error generating video stream URL:', error);
      throw new Error('Failed to generate video stream URL');
    }
  }

  /**
   * Set blob properties for optimized streaming
   * Sets content-disposition to inline and appropriate content-type
   */
  async setBlobForStreaming(containerName, blobName) {
    try {
      const containerClient = await this.getContainer(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);
      
      // Get file extension to determine content type
      const fileExtension = blobName.split('.').pop().toLowerCase();
      const contentType = this.getVideoContentType(fileExtension);
      
      await blockBlobClient.setHTTPHeaders({
        blobContentDisposition: 'inline',
        blobContentType: contentType,
        blobCacheControl: 'public, max-age=31536000', // Cache for 1 year
      });
      
      return true;
    } catch (error) {
      console.error('Error setting blob for streaming:', error);
      // Don't throw error as this is optimization, not critical
      return false;
    }
  }

  /**
   * Get appropriate content type for video files
   */
  getVideoContentType(extension) {
    const videoTypes = {
      'mp4': 'video/mp4',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'wmv': 'video/x-ms-wmv',
      'flv': 'video/x-flv',
      'webm': 'video/webm',
      'mkv': 'video/x-matroska',
      'm4v': 'video/x-m4v'
    };
    
    return videoTypes[extension] || 'video/mp4';
  }

  /**
   * Get video metadata including size for progress tracking
   */
  async getVideoMetadata(containerName, blobName) {
    try {
      const containerClient = await this.getContainer(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);
      
      const properties = await blockBlobClient.getProperties();
      
      return {
        size: properties.contentLength,
        lastModified: properties.lastModified,
        contentType: properties.contentType,
        etag: properties.etag,
        supportsRangeRequests: properties.acceptRanges === 'bytes'
      };
    } catch (error) {
      console.error('Error getting video metadata:', error);
      throw new Error('Failed to get video metadata');
    }
  }

  /**
   * Check if video blob exists and is accessible
   */
  async checkVideoAvailability(containerName, blobName) {
    try {
      const containerClient = await this.getContainer(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);
      
      const exists = await blockBlobClient.exists();
      return exists;
    } catch (error) {
      console.error('Error checking video availability:', error);
      return false;
    }
  }

  /**
   * Preload video for faster initial playback
   * Downloads just the first few MB for instant start
   */
  async preloadVideoChunk(containerName, blobName, chunkSize = 1024 * 1024 * 2) { // 2MB default
    try {
      const containerClient = await this.getContainer(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);
      
      // Download first chunk for preloading
      const downloadResponse = await blockBlobClient.download(0, chunkSize);
      const blob = await downloadResponse.blobBody;
      
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error preloading video chunk:', error);
      // Return null if preload fails, fallback to direct streaming
      return null;
    }
  }

  /**
   * Generate multiple quality stream URLs if available
   * This would be used if you have multiple quality versions of the same video
   */
  async generateMultiQualityStreams(containerName, baseVideoName) {
    const qualities = ['720p', '1080p', '480p', '360p'];
    const streams = {};
    
    for (let quality of qualities) {
      const qualityVideoName = baseVideoName.replace(/\.(mp4|avi|mov|wmv)$/i, `_${quality}.$1`);
      
      try {
        const exists = await this.checkVideoAvailability(containerName, qualityVideoName);
        if (exists) {
          streams[quality] = await this.generateVideoStreamUrl(containerName, qualityVideoName);
        }
      } catch (error) {
        console.log(`Quality ${quality} not available for ${baseVideoName}`);
      }
    }
    
    // If no quality versions found, return original
    if (Object.keys(streams).length === 0) {
      streams['original'] = await this.generateVideoStreamUrl(containerName, baseVideoName);
    }
    
    return streams;
  }

  async storeBlobContent(containerName, blobName, blobData) {
    try {
      let containerClient = await this.getContainer(containerName);
      let blockBlobClient = await containerClient.getBlockBlobClient(blobName);
      let storeBlobResponse = await blockBlobClient.uploadData(blobData);
      return storeBlobResponse.requestId;
    } catch (e) {
      throw "Azure Blob Content Store Failed";
    }
  }

  async updateBlobContent(
    containerName,
    blobName,
    newBlobData,
    newBlobDataLength
  ) {
    try {
      let containerClient = await this.getContainer(containerName);
      let blockBlobClient = await containerClient.getBlockBlobClient(blobName);
      let updateBlobResponse = await blockBlobClient.upload(
        newBlobData,
        newBlobDataLength
      );
      return updateBlobResponse.requestId;
    } catch (e) {
      throw "Azure Blob Update Failed";
    }
  }

  async downloadBlob(containerName, blobName) {
    try {
      let containerClient = await this.getContainer(containerName);
      let blockBlobClient = await containerClient.getBlockBlobClient(
        blobName.replace(/['"]+/g, "")
      );
      let downloadBlobResponse = await blockBlobClient.download(0);
      return await downloadBlobResponse.blobBody;
    } catch (e) {
      throw "Azure Blob Download Failed";
    }
  }

  async downloadMultipleBlobs(containerName, blobNames) {
    const maxRetries = 3;
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        let containerClient = await this.getContainer(containerName);
  
        // Create an array of promises for downloading each blob
        let downloadPromises = blobNames.map(async (blob) => {
          let blockBlobClient = await containerClient.getBlockBlobClient(
            blob.document_name.replace(/['"]+/g, "")
          );
          let downloadBlobResponse = await blockBlobClient.download(0);
          let blobBody = await downloadBlobResponse.blobBody;
          let blobUrl = URL.createObjectURL(blobBody);
  
          // Create an anchor element and click it to start download
          let a = document.createElement("a");
          a.href = blobUrl;
          a.download = blob.document_name;
          document.body.appendChild(a);
          a.click();
  
          // Cleanup
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
  
          return blobBody;
        });
  
        // Wait for all downloads to complete
        let downloadedBlobs = await Promise.all(downloadPromises);
        return downloadedBlobs;
      } catch (e) {
        if (attempt < maxRetries) {
          console.warn(`Download attempt ${attempt} failed. Retrying...`);
          await delay(1000); // Wait for 1 second before retrying
        } else {
          throw new Error("Azure Blob Download Failed after multiple attempts");
        }
      }
    }
  }
  
  async listBlobs(container, candidate) {
    try {
      store.dispatch({ type: testConstants.GET_MEDIA_REQUEST_MANAGE });
      let containerClient = await this.getContainer(container);
      let iter = containerClient.listBlobsByHierarchy("*", {
        prefix: "video/" + candidate,
      });
      let entity = await iter.next();
      let blobList = [];
      while (!entity.done) {
        let item = entity.value;
        if (item.kind === "prefix") {
        } else {
          blobList.push({ name: item.name });
        }
        entity = await iter.next();
      }
      store.dispatch({
        type: testConstants.GET_MEDIA_SUCCESS_MANAGE,
        content: blobList,
      });
      return blobList;
    } catch (e) {
      store.dispatch({
        type: testConstants.GET_MEDIA_FAILURE_MANAGE,
        error: e.toString(),
      });
    }
  }

  async downloadBlob(containerName, blobName) {
    let blobServiceClient = await this.getBlobServiceClient();
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blobClient = containerClient.getBlobClient(blobName);
    let downloadBlockBlobResponse = await blobClient.download();
    window.open(downloadBlockBlobResponse._response.request.url);
    return await downloadBlockBlobResponse.blobBody;
  }

  async getBlobContent(containerName, blobName) {
    let blobServiceClient = await this.getBlobServiceClient();
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blobClient = containerClient.getBlobClient(blobName);
    let downloadBlockBlobResponse = await blobClient.download();
    const downloadedBlobContent = await this.blobToString(
      await downloadBlockBlobResponse.blobBody
    );
    return await downloadedBlobContent;
  }

  async downloadBlobs(containerName, blobsList) {
    let blobServiceClient = await this.getBlobServiceClient();
    const containerClient = blobServiceClient.getContainerClient(containerName);

    let blobs = [];

    for (let blob of blobsList) {
      let blobClient = await containerClient.getBlobClient(blob.name);
      let response = await blobClient.download();
      let blobBody = await response.blobBody;
      blobs.push(blobBody);
    }

    let recordedBlob = new Blob(blobs, { type: "application/octet-stream" });
    return recordedBlob;
  }

  async deleteblob(containerName, blobName) {
    let blobServiceClient = await this.getBlobServiceClient();
    const containerClient = blobServiceClient.getContainerClient(containerName);
    await containerClient.deleteBlob(blobName);
  }

  async removeBlobContent(containerName, blobName) {
    let blobServiceClient = await this.getBlobServiceClient();
    const containerClient = blobServiceClient.getContainerClient(containerName);
    await containerClient.deleteBlob(blobName);
    await blobServiceClient.deleteContainer(containerName);
  }

  async streamToString(readableStream) {
    return new Promise((resolve, reject) => {
      const chunks = [];
      readableStream.on("data", (data) => {
        chunks.push(data.toString());
      });
      readableStream.on("end", () => {
        resolve(chunks.join(""));
      });
      readableStream.on("error", reject);
    });
  }

  async blobToString(blob) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.onload = () => {
        if (fileReader.result) {
          resolve(fileReader.result);
        } else {
          reject(new Error("Failed to read the blob as a string."));
        }
      };

      fileReader.onerror = () => {
        reject(new Error("Error reading the blob as a string."));
      };

      fileReader.readAsText(blob);
    });
  }
  
  generateSASUrl = async(containerName, blobName) => {
    const encodedBlobName = encodeURIComponent(blobName);
    SAS_TOKEN_MANAGE = DataHandler.getFromSession("SAS_TOKEN_MANAGE");
    if(!SAS_TOKEN_MANAGE){
      const sasToken = await this.getSASToken()
      SAS_TOKEN_MANAGE = sasToken.data
    }
    const blobUrl = `${appUrls.MANAGE_STORAGE_ACCOUNT_URI}${containerName}/${encodedBlobName}?${SAS_TOKEN_MANAGE}`;
    return blobUrl
  }

  setInline = async(containerName, blobName) => {
    const containerClient = await this.getContainer(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
  
    await blockBlobClient.setHTTPHeaders({
      blobContentDisposition: 'inline',
    });
  }

  /**
   * Upload blob with real-time progress tracking for large files
   * @param {string} containerName - Container name
   * @param {string} blobName - Blob name
   * @param {File|ArrayBuffer|Uint8Array} blobData - File data
   * @param {Function} onProgress - Progress callback function
   * @param {AbortController} abortController - Optional abort controller
   * @returns {Promise<string>} - Upload request ID
   */
  async uploadBlobWithProgress(containerName, blobName, blobData, onProgress, abortController) {
    const uploadId = `${containerName}_${blobName}_${Date.now()}`;
    
    try {
      // Store the abort controller and progress callback
      if (abortController) {
        this.uploadControllers.set(uploadId, abortController);
      }
      if (onProgress) {
        this.uploadProgressCallbacks.set(uploadId, onProgress);
      }

      const containerClient = await this.getContainer(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);

      // Convert File to ArrayBuffer if needed
      let fileBuffer;
      if (blobData instanceof File) {
        fileBuffer = await this.fileToArrayBuffer(blobData);
      } else {
        fileBuffer = blobData;
      }

      const fileSize = fileBuffer.byteLength;
      
      // For small files (< 100MB), use simple upload
      if (fileSize < 100 * 1024 * 1024) {
        return await this.uploadSmallFile(
          blockBlobClient, 
          fileBuffer, 
          fileSize, 
          uploadId, 
          abortController
        );
      }

      // For large files, use chunked upload
      return await this.uploadLargeFile(
        blockBlobClient, 
        fileBuffer, 
        fileSize, 
        uploadId, 
        abortController
      );

    } catch (error) {
      // Clean up tracking
      this.cleanupUpload(uploadId);
      
      if (error.name === 'AbortError') {
        throw new Error('Upload cancelled by user');
      }
      throw error;
    }
  }

  /**
   * Upload small files with progress tracking
   */
  async uploadSmallFile(blockBlobClient, fileBuffer, fileSize, uploadId, abortController) {
    const onProgress = this.uploadProgressCallbacks.get(uploadId);
    
    // Create a custom progress tracker
    let uploadedBytes = 0;
    const progressInterval = setInterval(() => {
      // Simulate incremental progress for small files
      if (uploadedBytes < fileSize * 0.9) {
        uploadedBytes += Math.min(fileSize * 0.1, fileSize - uploadedBytes);
        if (onProgress) {
          onProgress({
            loaded: uploadedBytes,
            total: fileSize,
            percentage: Math.round((uploadedBytes / fileSize) * 100)
          });
        }
      }
    }, 100);

    try {
      const uploadOptions = {
        abortSignal: abortController?.signal,
        onProgress: (progressEvent) => {
          if (onProgress && progressEvent.loadedBytes !== undefined) {
            onProgress({
              loaded: progressEvent.loadedBytes,
              total: fileSize,
              percentage: Math.round((progressEvent.loadedBytes / fileSize) * 100)
            });
          }
        }
      };

      const uploadResponse = await blockBlobClient.uploadData(fileBuffer, uploadOptions);
      
      clearInterval(progressInterval);
      
      // Ensure 100% progress
      if (onProgress) {
        onProgress({
          loaded: fileSize,
          total: fileSize,
          percentage: 100
        });
      }

      this.cleanupUpload(uploadId);
      return uploadResponse.requestId;

    } catch (error) {
      clearInterval(progressInterval);
      throw error;
    }
  }

  /**
   * Upload large files using block-based chunked upload
   */
  async uploadLargeFile(blockBlobClient, fileBuffer, fileSize, uploadId, abortController) {
    const onProgress = this.uploadProgressCallbacks.get(uploadId);
    const chunks = this.createChunks(fileBuffer, this.chunkSize);
    const blockIds = [];
    let uploadedBytes = 0;

    // Upload chunks with controlled concurrency
    const uploadPromises = [];
    const semaphore = new Semaphore(this.maxConcurrentChunks);

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const blockId = this.generateBlockId(i);
      blockIds.push(blockId);

      const uploadPromise = semaphore.acquire().then(async (release) => {
        try {
          // Check if upload was cancelled
          if (abortController?.signal.aborted) {
            throw new Error('Upload cancelled');
          }

          // Upload the chunk
          await blockBlobClient.stageBlock(blockId, chunk, chunk.byteLength, {
            abortSignal: abortController?.signal
          });

          // Update progress
          uploadedBytes += chunk.byteLength;
          if (onProgress) {
            onProgress({
              loaded: uploadedBytes,
              total: fileSize,
              percentage: Math.round((uploadedBytes / fileSize) * 100)
            });
          }

        } finally {
          release();
        }
      });

      uploadPromises.push(uploadPromise);
    }

    // Wait for all chunks to upload
    await Promise.all(uploadPromises);

    // Check if upload was cancelled before committing
    if (abortController?.signal.aborted) {
      throw new Error('Upload cancelled');
    }

    // Commit the block list
    const commitResponse = await blockBlobClient.commitBlockList(blockIds, {
      abortSignal: abortController?.signal
    });

    // Ensure 100% progress
    if (onProgress) {
      onProgress({
        loaded: fileSize,
        total: fileSize,
        percentage: 100
      });
    }

    this.cleanupUpload(uploadId);
    return commitResponse.requestId;
  }

  /**
   * Create chunks from file buffer
   */
  createChunks(buffer, chunkSize) {
    const chunks = [];
    let offset = 0;
    
    while (offset < buffer.byteLength) {
      const end = Math.min(offset + chunkSize, buffer.byteLength);
      chunks.push(new Uint8Array(buffer.slice(offset, end)));
      offset = end;
    }
    
    return chunks;
  }

  /**
   * Generate base64 encoded block ID
   */
  generateBlockId(index) {
    const blockId = `block-${index.toString().padStart(6, '0')}`;
    return btoa(blockId);
  }

  /**
   * Convert File to ArrayBuffer
   */
  fileToArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Clean up upload tracking
   */
  cleanupUpload(uploadId) {
    this.uploadControllers.delete(uploadId);
    this.uploadProgressCallbacks.delete(uploadId);
  }

  /**
   * Cancel upload by ID
   */
  cancelUpload(uploadId) {
    const controller = this.uploadControllers.get(uploadId);
    if (controller) {
      controller.abort();
    }
    this.cleanupUpload(uploadId);
  }

  /**
   * Legacy method for backward compatibility
   */
  async uploadBlob(containerName, blobName, blobData) {
    return this.uploadBlobWithProgress(containerName, blobName, blobData, null, null);
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * Semaphore class for controlling concurrent operations
 */
class Semaphore {
  constructor(max) {
    this.max = max;
    this.current = 0;
    this.queue = [];
  }

  async acquire() {
    return new Promise((resolve) => {
      if (this.current < this.max) {
        this.current++;
        resolve(() => this.release());
      } else {
        this.queue.push(resolve);
      }
    });
  }

  release() {
    this.current--;
    if (this.queue.length > 0) {
      this.current++;
      const next = this.queue.shift();
      next(() => this.release());
    }
  }

}


const AzureBlobStorage = new AzureStorageManage();
export { AzureBlobStorage as AzureStorageManage };
